<template>
  <div class="wrapper">
    <div class="card card-custom shadow-none">
      <div
        class="card-body d-flex align-items-center justify-content-between px-0"
      >
        <div class="input-icon">
          <input
            type="text"
            class="form-control rounded-pill"
            placeholder="Search..."
            :disabled="disabled"
            v-model="term"
            @input="search"
          />

          <span class="mt-1 mx-2">
            <i class="material-icons text-muted">search</i>
          </span>
        </div>

        <b-button
          variant="dark"
          class="btn-pill font-weight-bolder"
          :disabled="disabled"
          @click="create"
          v-if="$permissions.users.create"
          >New User</b-button
        >
      </div>
    </div>

    <div class="card card-custom shadow-none">
      <div class="card-body p-0">
        <div class="blockui shadow-sm zindex-1" v-if="querying">
          <span class="btn spinner spinner-lg spinner-dark spinner-left"
            >Loading...</span
          >
        </div>

        <div
          class="d-flex align-items-center justify-content-center"
          v-if="empty"
        >
          <span v-if="querying">...</span>
          <span v-else-if="error">{{ error }}</span>
          <span v-else>No results found.</span>
        </div>

        <div v-for="(object, i) in objects" :key="i">
          <div class="card card-custom bg-white border mb-4">
            <div
              class="card-body d-flex align-items-center justify-content-between p-4"
            >
              <div class="d-flex">
                <div class="d-flex align-items-center">
                  <div
                    class="symbol symbol-70 symbol-circle symbol-light border"
                  >
                    <img alt="image" :src="object.image" v-if="object.image" />

                    <span class="symbol-label" v-else />
                  </div>

                  <div class="ml-3">
                    <div class="font-weight-bolder font-size-lg">
                      {{ object.first_name }} {{ object.last_name }}
                    </div>
                    <div class="text-black-50 font-size-sm font-weight-bold">
                      {{ object.email }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="d-flex align-items-center">
                <div
                  class="d-flex align-items-center justify-content-center"
                  style="width: 100px"
                >
                  <div
                    class="label label-inline label-light-danger font-weight-bolder py-4"
                    v-if="!object.is_active"
                  >
                    INACTIVE
                  </div>
                  <div
                    class="label label-inline label-light-success font-weight-bolder py-4"
                    v-else
                  >
                    ACTIVE
                  </div>
                </div>

                <div
                  class="d-flex align-items-center justify-content-center mr-4"
                  style="width: 50px"
                >
                  <span>
                    <button
                      class="btn btn-icon btn-circle btn-hover-light-danger"
                      title="Deactivate"
                      :disabled="disabled"
                      v-b-tooltip.hover
                      @click="confirmToggle(object)"
                      v-if="object.is_active && $permissions.users.deactivate"
                    >
                      <i class="material-icons icon-2x">toggle_on</i>
                    </button>

                    <button
                      class="btn btn-icon btn-circle btn-hover-light-primary"
                      title="Activate"
                      :disabled="disabled"
                      v-b-tooltip.hover
                      @click="confirmToggle(object)"
                      v-if="!object.is_active && $permissions.users.activate"
                    >
                      <i class="material-icons icon-2x">toggle_off</i>
                    </button>
                  </span>
                </div>

                <button
                  class="btn btn-icon btn-circle btn-hover-dark mr-4"
                  title="Preview"
                  :disabled="disabled"
                  @click="view(object)"
                  v-b-tooltip.hover
                >
                  <i class="material-icons icon-lg">visibility</i>
                </button>

                <button
                  class="btn btn-icon btn-circle btn-hover-dark mr-4"
                  title="Edit"
                  :disabled="disabled"
                  @click.stop="update(object)"
                  v-b-tooltip.hover
                  v-if="$permissions.users.update"
                >
                  <i class="material-icons icon-lg">edit</i>
                </button>

                <button
                  class="btn btn-icon btn-circle btn-hover-dark"
                  title="Delete"
                  :disabled="disabled"
                  @click.stop="
                    confirm(object, `${object.first_name} ${object.last_name}`)
                  "
                  v-b-tooltip.hover
                  v-if="$permissions.users.delete"
                >
                  <i class="material-icons icon-lg">delete</i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <paginator :disabled="disabled" :size="page_size" :total="total" />

        <form-modal ref="form_modal" />
        <c-details ref="details-modal" />
      </div>
    </div>
  </div>
</template>

<script>
import Paginator from "@/components/paginator.vue";
import UsersService from "@/services/users.service";

import FormModal from "./components/form";
import CDetails from "./components/details";

export default {
  name: "Users",
  components: {
    Paginator,
    FormModal,
    CDetails,
  },
  methods: {
    search() {
      clearTimeout(this.debounce);

      this.debounce = setTimeout(() => {
        let params = {};

        if (this.term) params.search = this.term;

        this.query(params);
      }, 500);
    },
    query(params = {}) {
      params["page"] = this.$route.query.page || 1;
      params["page_size"] = this.page_size;

      if (this.term) params.search = this.term;

      this.error = null;
      this.querying = true;

      UsersService.query(params)
        .then(({ count, data }) => {
          this.total = count;
          this.objects = data.map((item) => {
            return item;
          });
        })
        .catch(({ message }) => {
          this.error = "There was an error loading users.";
        })
        .finally(() => {
          this.querying = false;
        });
    },
    confirm(object, name) {
      let message = this.$createElement("span", [
        `Are you sure you want to delete "`,
        this.$createElement("strong", name),
        `"?`,
      ]);

      this.$bvModal
        .msgBoxConfirm(message, {
          title: "Confirm delete",
          buttonSize: "md",
          okTitle: "Delete",
          okVariant: "dark btn-pill font-weight-bolder",
          cancelTitle: "Cancel",
          cancelVariant: "secondary btn-pill font-weight-bolder",
          centered: true,
        })
        .then((confirmed) => {
          if (confirmed) this.remove(object);
        });
    },
    remove(object) {
      this.deleting = true;

      UsersService.delete(object.id)
        .then((response) => {
          this.$bvModal
            .msgBoxOk(`User deleted successfully.`, {
              title: "Success",
              buttonSize: "md",
              okTitle: "Ok",
              okVariant: "dark btn-pill font-weight-bolder",
              centered: true,
            })
            .then((value) => {
              this.query();
            });
        })
        .catch(({ message }) => {
          this.$bvModal.msgBoxOk(message, {
            title: "Error",
            buttonSize: "md",
            okTitle: "Ok",
            okVariant: "dark btn-pill font-weight-bolder",
            centered: true,
          });
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    async toggle(object) {
      this.sending = true;

      const payload = {
        is_active: !object.is_active,
      };

      UsersService.patch(object.id, payload)
        .then(({ data }) => {
          this.$bvModal
            .msgBoxOk(
              `Account ${
                object.is_active ? "deactivated" : "activated"
              } successfully.`,
              {
                title: "Success",
                buttonSize: "md",
                okTitle: "Ok",
                okVariant: "dark btn-pill font-weight-bolder",
                centered: true,
              }
            )
            .then((value) => {
              this.query();
            });
        })
        .catch(({ message }) => {
          this.$bvModal.msgBoxOk(message, {
            title: "Error",
            buttonSize: "md",
            okTitle: "Ok",
            okVariant: "dark btn-pill font-weight-bolder",
            centered: true,
          });
        })
        .finally(() => {
          this.sending = false;
        });
    },
    confirmToggle(object) {
      let message = this.$createElement("span", [
        `Are you sure you want to ${
          object.is_active ? "deactivate" : "activate"
        } "`,
        this.$createElement(
          "strong",
          `${object.first_name} ${object.last_name}" `
        ),
        `account?`,
      ]);

      this.$bvModal
        .msgBoxConfirm(message, {
          title: `Confirm ${object.is_active ? "deactivation" : "activation"}`,
          buttonSize: "md",
          okTitle: object.is_active ? "Deactivate" : "Activate",
          okVariant: "dark btn-pill font-weight-bolder",
          cancelTitle: "Cancel",
          cancelVariant: "secondary btn-pill font-weight-bolder",
          centered: true,
        })
        .then((confirmed) => {
          if (confirmed) this.toggle(object);
        });
    },
    view(object) {
      this.$refs["details-modal"].open(object.id);
    },
    create() {
      this.$refs.form_modal.show();
    },
    update(object) {
      this.$refs.form_modal.show(object.id);
    },
  },
  watch: {
    "$route.query.page"(value) {
      this.query();
    },
  },
  computed: {
    empty() {
      return this.objects.length === 0;
    },
    disabled() {
      return this.querying || this.deleting;
    },
  },
  data() {
    return {
      debounce: null,
      term: null,
      error: null,
      querying: false,
      deleting: false,
      total: 0,
      page_size: 20,
      objects: [],
    };
  },
  mounted() {
    if (!this.$permissions.users.read) this.$router.push({ name: "403" });
    this.query();
  },
};
</script>
